import { useState, useEffect, useMemo } from 'react';
import {
  ShieldCheck, Plus, Trash2, RefreshCw, AlertTriangle, CheckCircle, Clock,
  Lock, KeyRound, Wallet, Activity, X, Loader2, Eye, EyeOff,
} from 'lucide-react';
import { useAccount, useChainId, useSignTypedData } from 'wagmi';
import { Spinner } from '../components/ui';
import {
  getWalletSessionPolicy,
  prepareWalletSession,
  createWalletSession,
  listWalletSessions,
  getWalletSession,
  revokeWalletSession,
} from '../services/api';

// Common token quick-pick presets per chain. Users can still add any ERC-20
// via the custom address input below — these are just one-tap shortcuts for
// the 6-8 most-traded tokens on each chain. Session caps allow up to 20.
const COMMON_TOKENS_BY_CHAIN = {
  1: [ // Ethereum mainnet
    { symbol: 'WETH', address: '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2' },
    { symbol: 'USDC', address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48' },
    { symbol: 'USDT', address: '0xdAC17F958D2ee523a2206206994597C13D831ec7' },
    { symbol: 'WBTC', address: '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599' },
    { symbol: 'DAI',  address: '0x6B175474E89094C44Da98b954EedeAC495271d0F' },
    { symbol: 'LINK', address: '0x514910771AF9Ca656af840dff83E8264EcF986CA' },
    { symbol: 'UNI',  address: '0x1f9840a85d5aF5bf1D1762F925BDADdC4201F984' },
    { symbol: 'AAVE', address: '0x7Fc66500c84A76Ad7e9c93437bFc5Ac33E2DDaE9' },
    { symbol: 'PEPE', address: '0x6982508145454Ce325dDbE47a25d4ec3d2311933' },
    { symbol: 'SHIB', address: '0x95aD61b0a150d79219dCF64E1E6Cc01f0B64C4cE' },
  ],
  8453: [ // Base
    { symbol: 'WETH',  address: '0x4200000000000000000000000000000000000006' },
    { symbol: 'USDC',  address: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913' },
    { symbol: 'USDbC', address: '0xd9aAEc86B65D86f6A7B5B1b0c42FFA531710b6CA' },
    { symbol: 'cbETH', address: '0x2Ae3F1Ec7F1F5012CFEab0185bfc7aa3cf0DEc22' },
    { symbol: 'DAI',   address: '0x50c5725949A6F0c72E6C4a641F24049A917DB0Cb' },
  ],
  137: [ // Polygon
    { symbol: 'WMATIC', address: '0x0d500B1d8E8eF31E21C99d1Db9A6444d3ADf1270' },
    { symbol: 'USDC',   address: '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359' },
    { symbol: 'USDT',   address: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F' },
    { symbol: 'WETH',   address: '0x7ceB23fD6bC0adD59E62ac25578270cFf1b9f619' },
    { symbol: 'DAI',    address: '0x8f3Cf7ad23Cd3CaDbD9735AFf958023239c6A063' },
    { symbol: 'WBTC',   address: '0x1BFD67037B42Cf73acF2047067bd4F2C47D9BfD6' },
    { symbol: 'LINK',   address: '0x53E0bca35eC356BD5ddDFebbD1Fc0fD03FaBad39' },
  ],
  42161: [ // Arbitrum
    { symbol: 'WETH', address: '0x82aF49447D8a07e3bd95BD0d56f35241523fBab1' },
    { symbol: 'USDC', address: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831' },
    { symbol: 'USDT', address: '0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9' },
    { symbol: 'ARB',  address: '0x912CE59144191C1204E64559FE8253a0e49E6548' },
    { symbol: 'WBTC', address: '0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f' },
    { symbol: 'DAI',  address: '0xDA10009cBd5D07dd0CeCc66161FC93D7c9000da1' },
  ],
  10: [ // Optimism
    { symbol: 'WETH', address: '0x4200000000000000000000000000000000000006' },
    { symbol: 'USDC', address: '0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85' },
    { symbol: 'USDT', address: '0x94b008aA00579c1307B0EF2c499aD98a8ce58e58' },
    { symbol: 'OP',   address: '0x4200000000000000000000000000000000000042' },
    { symbol: 'WBTC', address: '0x68f180fcCe6836688e9084f035309E29Bf0A2095' },
    { symbol: 'DAI',  address: '0xDA10009cBd5D07dd0CeCc66161FC93D7c9000da1' },
  ],
  56: [ // BSC
    { symbol: 'WBNB', address: '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c' },
    { symbol: 'USDT', address: '0x55d398326f99059fF775485246999027B3197955' },
    { symbol: 'USDC', address: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d' },
    { symbol: 'BUSD', address: '0xe9e7CEA3DedcA5984780Bafc599bD69ADd087D56' },
    { symbol: 'WETH', address: '0x2170Ed0880ac9A755fd29B2688956BD959F933F8' },
    { symbol: 'CAKE', address: '0x0E09FaBB73Bd3Ade0a17ECC321fD13a19e81cE82' },
  ],
  43114: [ // Avalanche C-Chain
    { symbol: 'WAVAX', address: '0xB31f66AA3C1e785363F0875A1B74E27b85FD66c7' },
    { symbol: 'USDC',  address: '0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E' },
    { symbol: 'USDT',  address: '0x9702230A8Ea53601f5cD2dc00fDBc13d4dF4A8c7' },
    { symbol: 'WETH.e',address: '0x49D5c2BdFfac6CE2BFdB6640F4F80f226bc10bAB' },
    { symbol: 'BTC.b', address: '0x152b9d0FdC40C096757F570A51E494bd4b943E50' },
    { symbol: 'DAI.e', address: '0xd586E7F844cEa2F87f50152665BCbc2C279D8d70' },
  ],
};

const HOUR = 3600;
const DAY = 86400;
const EXPIRY_OPTIONS = [
  { label: '1 hour',  value: 1 * HOUR },
  { label: '24 hours', value: 1 * DAY },
  { label: '7 days',  value: 7 * DAY },
  { label: '30 days (max)', value: 30 * DAY },
];

const ACTION_OPTIONS = [
  { id: 'swap',        label: 'Token swap',          desc: 'DEX-aggregated token-for-token swaps within the allowed set' },
  { id: 'limit_order', label: 'Limit orders',         desc: 'Post limit orders that fill when price hits the trigger' },
  { id: 'rebalance',   label: 'Portfolio rebalance', desc: 'Rebalance allocations within the allowed token set' },
];

function Card({ children, className = '' }) {
  return <div className={`bg-dark-800 rounded-2xl p-6 border border-dark-700/50 ${className}`}>{children}</div>;
}

function Toast({ message, type = 'success', onDismiss }) {
  useEffect(() => { const t = setTimeout(onDismiss, 6000); return () => clearTimeout(t); }, [onDismiss]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl shadow-2xl text-sm font-medium flex items-center gap-2
      ${type === 'success' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
        : type === 'error' ? 'bg-red-500/20 text-red-300 border border-red-500/30'
        : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'}`}>
      {message}
    </div>
  );
}

function shortenAddr(a) { if (!a) return '—'; return `${a.slice(0, 6)}…${a.slice(-4)}`; }

// ─── Session creation wizard ─────────────────────────────────────────────────

function SessionWizard({ policy, onCreated, onCancel, onToast }) {
  const { address: connectedAddr, isConnected } = useAccount();
  const connectedChainId = useChainId();
  const { signTypedDataAsync, isPending: signing } = useSignTypedData();

  const [chainId, setChainId] = useState(connectedChainId || 1);
  const [expirySec, setExpirySec] = useState(7 * DAY);
  const [allowedTokens, setAllowedTokens] = useState(() => {
    const list = COMMON_TOKENS_BY_CHAIN[chainId] || COMMON_TOKENS_BY_CHAIN[1];
    return list.slice(0, 2).map(t => t.address);
  });
  const [customToken, setCustomToken] = useState('');
  const [allowedActions, setAllowedActions] = useState(['swap']);
  const [perTradeMaxUsd, setPerTradeMaxUsd] = useState(50);
  const [totalSessionMaxUsd, setTotalSessionMaxUsd] = useState(500);
  const [dailyMaxUsd, setDailyMaxUsd] = useState(200);
  const [maxTradesPerDay, setMaxTradesPerDay] = useState(10);
  const [submitting, setSubmitting] = useState(false);

  const commonTokens = COMMON_TOKENS_BY_CHAIN[chainId] || [];

  // When chain changes, replace token whitelist with that chain's common tokens
  useEffect(() => {
    const list = COMMON_TOKENS_BY_CHAIN[chainId] || [];
    setAllowedTokens(list.slice(0, 2).map(t => t.address));
  }, [chainId]);

  function toggleToken(addr) {
    setAllowedTokens(prev => prev.includes(addr) ? prev.filter(a => a !== addr) : [...prev, addr]);
  }
  function addCustomToken() {
    const a = customToken.trim();
    if (!/^0x[0-9a-fA-F]{40}$/.test(a)) { onToast({ type: 'error', message: 'Invalid 0x address' }); return; }
    if (allowedTokens.includes(a)) return;
    setAllowedTokens([...allowedTokens, a]);
    setCustomToken('');
  }
  function toggleAction(id) {
    setAllowedActions(prev => prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]);
  }

  const canSign =
    isConnected &&
    connectedAddr &&
    Number(connectedChainId) === Number(chainId) &&
    allowedTokens.length > 0 &&
    allowedActions.length > 0 &&
    Number(perTradeMaxUsd) > 0 &&
    Number(dailyMaxUsd) >= Number(perTradeMaxUsd) &&
    Number(totalSessionMaxUsd) >= Number(dailyMaxUsd) &&
    Number(maxTradesPerDay) >= 1 &&
    !signing &&
    !submitting;

  async function handleSign() {
    if (!canSign) return;
    setSubmitting(true);
    try {
      // 1. Ask backend to build the typed-data + reserved sessionId
      const prep = await prepareWalletSession({
        wallet: connectedAddr,
        chainId,
        expirySec,
        allowedTokens,
        allowedActions,
        perTradeMaxUsd,
        totalSessionMaxUsd,
        dailyMaxUsd,
        maxTradesPerDay,
      });

      // 2. Sign with wagmi — this opens the wallet's typed-data signing dialog
      const { typedData } = prep.data;
      const signature = await signTypedDataAsync({
        domain: typedData.domain,
        types: typedData.types,
        primaryType: typedData.primaryType,
        message: typedData.message,
      });

      // 3. Submit signed session — backend verifies signature server-side
      const created = await createWalletSession({
        message: typedData.message,
        signature,
        chainId,
      });

      onToast({ type: 'success', message: `Session created — bounded by ${allowedTokens.length} tokens, $${perTradeMaxUsd} per trade` });
      onCreated(created.data?.session);
    } catch (err) {
      onToast({ type: 'error', message: err?.shortMessage || err?.message || 'Could not create session' });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-emerald-400" /> New wallet session
        </h3>
        <button onClick={onCancel} className="text-gray-500 hover:text-white"><X className="w-4 h-4" /></button>
      </div>

      {!isConnected && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl px-4 py-3 mb-4 flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-amber-300 text-sm font-semibold">Connect your wallet first</p>
            <p className="text-amber-300/80 text-xs mt-0.5">Use the Connect Wallet button in the header. We'll never ask for your private key.</p>
          </div>
        </div>
      )}

      {isConnected && Number(connectedChainId) !== Number(chainId) && (
        <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl px-4 py-3 mb-4 flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
          <p className="text-blue-300 text-sm">Wallet is on chain {connectedChainId}; session is for chain {chainId}. Switch your wallet to {policy?.supportedChains?.[chainId] || chainId} or change the session chain to match.</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1.5">Wallet</label>
          <div className="bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm font-mono">
            {connectedAddr ? shortenAddr(connectedAddr) : 'Not connected'}
          </div>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1.5">Chain</label>
          <select value={chainId} onChange={e => setChainId(Number(e.target.value))}
            className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:border-accent-primary focus:outline-none">
            {Object.entries(policy?.supportedChains || {}).map(([id, name]) => (
              <option key={id} value={id}>{name} (#{id})</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1.5">Expiry</label>
          <select value={expirySec} onChange={e => setExpirySec(Number(e.target.value))}
            className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:border-accent-primary focus:outline-none">
            {EXPIRY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1.5">Max trades / day</label>
          <input type="number" min={1} max={1000} value={maxTradesPerDay}
            onChange={e => setMaxTradesPerDay(Number(e.target.value))}
            className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:border-accent-primary focus:outline-none" />
        </div>
      </div>

      <div className="border-t border-dark-700/50 pt-4 mb-4">
        <h4 className="text-xs uppercase tracking-wider text-gray-400 font-bold mb-3">Allowed tokens <span className="text-gray-600 font-normal normal-case">· up to 20 per session</span></h4>
        <p className="text-[11px] text-gray-500 mb-3">
          Quick-pick the common tokens for this chain below, or paste any ERC-20 contract address in the input — including tokens you minted yourself.
          The session will only authorize swaps among these tokens; anything else is rejected by the cap engine.
        </p>
        <div className="flex flex-wrap gap-2 mb-3">
          {commonTokens.map(t => {
            const on = allowedTokens.includes(t.address);
            return (
              <button key={t.address} onClick={() => toggleToken(t.address)}
                className={`text-xs font-mono px-3 py-1.5 rounded-lg border ${on ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' : 'bg-dark-700 text-gray-400 border-dark-600'}`}>
                {on ? '✓ ' : ''}{t.symbol}
              </button>
            );
          })}
        </div>
        {allowedTokens.filter(a => !commonTokens.find(t => t.address.toLowerCase() === a.toLowerCase())).map(a => (
          <div key={a} className="flex items-center gap-2 text-[11px] font-mono text-gray-400 mb-1">
            <span className="text-emerald-400">✓</span> {a}
            <button onClick={() => toggleToken(a)} className="text-gray-600 hover:text-red-400 ml-auto"><X className="w-3 h-3" /></button>
          </div>
        ))}
        <div className="flex gap-2 mt-2">
          <input value={customToken} onChange={e => setCustomToken(e.target.value)} placeholder="Custom token 0x..."
            className="flex-1 bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-xs text-white font-mono placeholder-gray-600" />
          <button onClick={addCustomToken} disabled={!customToken.trim()}
            className="px-3 py-2 rounded-lg text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 disabled:opacity-30">
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="border-t border-dark-700/50 pt-4 mb-4">
        <h4 className="text-xs uppercase tracking-wider text-gray-400 font-bold mb-3">Allowed actions</h4>
        <div className="space-y-2">
          {ACTION_OPTIONS.map(a => {
            const on = allowedActions.includes(a.id);
            return (
              <label key={a.id} className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer ${on ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-dark-700/30 border-dark-600/50'}`}>
                <input type="checkbox" checked={on} onChange={() => toggleAction(a.id)} className="accent-emerald-500 mt-0.5" />
                <div>
                  <p className="text-sm text-white font-medium">{a.label}</p>
                  <p className="text-[11px] text-gray-500">{a.desc}</p>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      <div className="border-t border-dark-700/50 pt-4 mb-4">
        <h4 className="text-xs uppercase tracking-wider text-gray-400 font-bold mb-3">USD caps</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">Per trade max ($)</label>
            <input type="number" min={1} max={policy?.hardPerTradeMaxUsd ?? 100000} value={perTradeMaxUsd}
              onChange={e => setPerTradeMaxUsd(Number(e.target.value))}
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:border-accent-primary focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">Daily max ($)</label>
            <input type="number" min={perTradeMaxUsd} value={dailyMaxUsd}
              onChange={e => setDailyMaxUsd(Number(e.target.value))}
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:border-accent-primary focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">Total session max ($)</label>
            <input type="number" min={dailyMaxUsd} max={policy?.hardTotalSessionMaxUsd ?? 1000000} value={totalSessionMaxUsd}
              onChange={e => setTotalSessionMaxUsd(Number(e.target.value))}
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:border-accent-primary focus:outline-none" />
          </div>
        </div>
        <p className="text-[11px] text-gray-500 mt-2">
          haythix enforces these caps server-side on every trade staged from this session. Re-signing is the only way to raise them.
        </p>
      </div>

      <div className="bg-amber-500/5 border border-amber-500/15 rounded-xl px-4 py-3 mb-4">
        <p className="text-[11px] text-amber-300/90">
          <strong>What gets rejected automatically:</strong> {(policy?.forbiddenOperations || []).join(', ')}.
          The session signature includes these as hardcoded forbidden operations — a malicious frontend or backend can't override them without a new signature from you.
        </p>
      </div>

      <div className="flex justify-end gap-3">
        <button onClick={onCancel} className="px-4 py-2 rounded-xl text-sm text-gray-400 border border-dark-600 hover:text-white">Cancel</button>
        <button onClick={handleSign} disabled={!canSign}
          className="px-4 py-2 rounded-xl text-sm font-semibold bg-emerald-500 text-dark-900 hover:bg-emerald-400 disabled:opacity-40 flex items-center gap-2">
          {submitting || signing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
          {submitting || signing ? 'Awaiting wallet signature…' : 'Sign with wallet'}
        </button>
      </div>
    </Card>
  );
}

// ─── Active session row ──────────────────────────────────────────────────────

function SessionRow({ session, onRevoke, onView, busy }) {
  const expSec = Number(session.expiresAt);
  const expDate = new Date(expSec * 1000);
  const isExpired = Date.now() / 1000 >= expSec;
  const isRevoked = !!session.revokedAt;
  const isActive = !isExpired && !isRevoked;

  const status = isRevoked ? { label: 'REVOKED', color: 'text-red-400', bg: 'bg-red-500/15' }
    : isExpired ? { label: 'EXPIRED', color: 'text-gray-400', bg: 'bg-gray-500/15' }
    : { label: 'ACTIVE', color: 'text-emerald-400', bg: 'bg-emerald-500/15' };

  return (
    <Card>
      <div className="flex items-start justify-between mb-3 gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${status.bg} ${status.color}`}>{status.label}</span>
            <span className="text-[10px] text-gray-500">{session.chainName} (#{session.chainId})</span>
            <span className="text-[10px] text-gray-500 font-mono">{shortenAddr(session.walletAddress)}</span>
          </div>
          <p className="text-xs text-gray-400">
            {session.allowedTokens?.length || 0} tokens · {session.allowedActions} ·
            ${session.perTradeMaxUsd}/trade · ${session.dailyMaxUsd}/day · ${session.totalSessionMaxUsd}/session
          </p>
          <p className="text-[10px] text-gray-600 mt-1">
            {isExpired ? `Expired ${expDate.toLocaleString()}` : `Expires ${expDate.toLocaleString()}`}
          </p>
          {isActive && (
            <p className="text-[10px] text-amber-400/80 mt-1 italic">
              0 trades recorded · session is declarative until v2 (Permit2) ships
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={() => onView(session)} title="Inspect"
            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-dark-700"><Eye className="w-3.5 h-3.5" /></button>
          {isActive && (
            <button onClick={() => onRevoke(session)} disabled={busy === session.sessionId}
              title="Revoke"
              className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 disabled:opacity-40">
              {busy === session.sessionId ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            </button>
          )}
        </div>
      </div>
    </Card>
  );
}

// ─── Detail panel ────────────────────────────────────────────────────────────

function SessionDetail({ session, onClose }) {
  const [showSig, setShowSig] = useState(false);
  if (!session) return null;
  const usage = session.usage || { totalSpent: 0, totalTrades: 0, last24hSpent: 0, last24hTrades: 0, capsRemaining: {}, recent: [] };

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
          <Activity className="w-4 h-4 text-emerald-400" /> Session usage
        </h3>
        <button onClick={onClose} className="text-gray-500 hover:text-white"><X className="w-4 h-4" /></button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-dark-700/40 rounded-xl p-3">
          <p className="text-[10px] uppercase text-gray-500 mb-1">Total spent</p>
          <p className="text-lg font-bold text-white">${usage.totalSpent.toLocaleString()}</p>
          <p className="text-[10px] text-gray-600">of ${session.message.totalSessionMaxUsd}</p>
        </div>
        <div className="bg-dark-700/40 rounded-xl p-3">
          <p className="text-[10px] uppercase text-gray-500 mb-1">Last 24h</p>
          <p className="text-lg font-bold text-white">${usage.last24hSpent.toLocaleString()}</p>
          <p className="text-[10px] text-gray-600">of ${session.message.dailyMaxUsd}</p>
        </div>
        <div className="bg-dark-700/40 rounded-xl p-3">
          <p className="text-[10px] uppercase text-gray-500 mb-1">Trades total</p>
          <p className="text-lg font-bold text-white">{usage.totalTrades}</p>
        </div>
        <div className="bg-dark-700/40 rounded-xl p-3">
          <p className="text-[10px] uppercase text-gray-500 mb-1">Trades 24h</p>
          <p className="text-lg font-bold text-white">{usage.last24hTrades}</p>
          <p className="text-[10px] text-gray-600">of {session.message.maxTradesPerDay}</p>
        </div>
      </div>

      <div className="bg-dark-700/30 rounded-xl p-3 mb-4">
        <p className="text-[10px] uppercase text-gray-500 mb-2">Allowed tokens ({session.message.allowedTokens.length})</p>
        <div className="flex flex-wrap gap-1.5">
          {session.message.allowedTokens.map(t => (
            <span key={t} className="text-[10px] font-mono bg-dark-700 text-gray-400 px-2 py-0.5 rounded">{shortenAddr(t)}</span>
          ))}
        </div>
      </div>

      {usage.recent.length > 0 && (
        <div className="bg-dark-700/30 rounded-xl p-3 mb-4">
          <p className="text-[10px] uppercase text-gray-500 mb-2">Recent fills</p>
          <div className="space-y-1.5">
            {usage.recent.map((e, i) => (
              <div key={i} className="flex items-center justify-between text-[11px]">
                <span className="text-gray-500">{new Date(e.recordedAt).toLocaleString()}</span>
                <span className="text-white font-mono">{e.action}</span>
                <span className="text-emerald-400 font-mono">${Number(e.usdAmount).toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <details className="bg-dark-700/30 rounded-xl">
        <summary className="cursor-pointer p-3 text-xs text-gray-400 hover:text-white flex items-center gap-2">
          <Lock className="w-3 h-3" /> Signed authorization (transparency)
        </summary>
        <div className="p-3 border-t border-dark-700/50 text-[10px] font-mono break-all">
          <p className="text-gray-500 mb-1">Signature</p>
          <button onClick={() => setShowSig(s => !s)} className="text-emerald-400 hover:underline mb-2 flex items-center gap-1">
            {showSig ? <><EyeOff className="w-3 h-3" /> Hide</> : <><Eye className="w-3 h-3" /> Show</>}
          </button>
          {showSig && <p className="text-gray-400 mb-3">{session.signature}</p>}
          <p className="text-gray-500 mb-1">Recovered address (must equal wallet)</p>
          <p className="text-gray-400 mb-3">{session.recoveredAddress}</p>
          <p className="text-gray-500 mb-1">Forbidden operations (signed-in floor)</p>
          <p className="text-gray-400">{session.message.forbiddenOperations}</p>
        </div>
      </details>
    </Card>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

export default function WalletSessionPage() {
  const [policy, setPolicy] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [detailFor, setDetailFor] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [pol, list] = await Promise.all([
        getWalletSessionPolicy().catch(() => null),
        listWalletSessions().catch(() => ({ sessions: [] })),
      ]);
      setPolicy(pol?.data || pol);
      setSessions(list?.data?.sessions || list?.sessions || []);
    } catch (err) {
      setToast({ type: 'error', message: err.message });
    }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function handleRevoke(session) {
    if (!window.confirm(`Revoke session for ${shortenAddr(session.walletAddress)} on ${session.chainName}? Active automation stops immediately.`)) return;
    setBusyId(session.sessionId);
    try {
      await revokeWalletSession(session.sessionId, 'user-revoked');
      setToast({ type: 'success', message: 'Session revoked' });
      await load();
    } catch (err) {
      setToast({ type: 'error', message: err.message });
    }
    setBusyId(null);
  }

  async function handleView(session) {
    try {
      const res = await getWalletSession(session.sessionId);
      setDetailFor(res?.data?.session);
    } catch (err) {
      setToast({ type: 'error', message: err.message });
    }
  }

  const activeCount = useMemo(() =>
    sessions.filter(s => !s.revokedAt && Number(s.expiresAt) > Date.now() / 1000).length,
  [sessions]);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-accent-primary flex items-center gap-2">
            <ShieldCheck className="w-6 h-6" /> Wallet Sessions
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
              NON-CUSTODIAL
            </span>
          </h1>
          <p className="text-gray-400 text-sm mt-1 max-w-3xl leading-relaxed">
            Authorize haythix to stage trades from your wallet within strict limits — without ever sharing your keys.
            Every session is a signed EIP-712 message with hard caps on tokens, USD amounts, daily limits, and forbidden operations.
            <strong className="text-emerald-400"> One signature to revoke, zero key exposure.</strong>
          </p>
        </div>
        <button onClick={() => setShowWizard(true)}
          className="px-4 py-2 rounded-xl text-sm font-semibold bg-emerald-500 text-dark-900 hover:bg-emerald-400 flex items-center gap-2">
          <Plus className="w-4 h-4" /> New session
        </button>
      </div>

      {/* Trust banner */}
      <Card className="!p-4 border-emerald-500/20 bg-emerald-500/5">
        <div className="flex items-start gap-3">
          <Wallet className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-emerald-300">How this differs from "connect API key"</p>
            <ul className="text-xs text-emerald-300/80 mt-1 space-y-0.5 list-disc list-inside">
              <li>Your private key never leaves your wallet — we receive only a signed authorization message.</li>
              <li>The signature explicitly forbids withdrawals, blanket approvals, and arbitrary execution.</li>
              <li>Cap structure (per-trade $, daily $, total $, trade count) is signed by you and stored cryptographically.</li>
              <li>Revoke takes effect on the next request — no on-chain transaction needed for revocation in v1.</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Honesty banner — what v1 sessions DO and DON'T do today.
          Sessions are a cryptographic declaration; trade-by-trade signing still
          required until Permit2 ships in v2. */}
      <Card className="!p-4 border-amber-500/20 bg-amber-500/5">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-amber-200/90 leading-relaxed">
            <p className="font-semibold text-amber-300 mb-1">v1 — declarative session framework</p>
            <p>
              Sessions you create here cryptographically declare which trade shapes you authorize.
              Today, <strong>each individual trade still requires your wallet signature</strong> — sessions
              are a pre-commitment, not an autopilot. Permit2 / ERC-4337 session-key integration ships in
              v2; once it does, the same sessions you create now will enforce caps automatically on
              one-tap trades. Nothing about your sessions changes at that upgrade.
            </p>
          </div>
        </div>
      </Card>

      {showWizard && policy && (
        <SessionWizard
          policy={policy}
          onCreated={() => { setShowWizard(false); load(); }}
          onCancel={() => setShowWizard(false)}
          onToast={setToast}
        />
      )}

      {detailFor && (
        <SessionDetail session={detailFor} onClose={() => setDetailFor(null)} />
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20"><Spinner className="text-accent-primary" /></div>
      ) : sessions.length === 0 ? (
        <Card className="text-center py-16">
          <KeyRound className="w-10 h-10 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">No wallet sessions yet.</p>
          <p className="text-gray-600 text-xs mt-1">Create one to authorize bounded automation from your wallet.</p>
        </Card>
      ) : (
        <>
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>{activeCount} active · {sessions.length} total</span>
            <button onClick={load} className="hover:text-white flex items-center gap-1">
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {sessions.map(s => (
              <SessionRow
                key={s.sessionId}
                session={s}
                busy={busyId}
                onRevoke={handleRevoke}
                onView={handleView}
              />
            ))}
          </div>
        </>
      )}

      {toast && <Toast {...toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}
